import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, Copy, Activity } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllSessions } from '../api/sessions';
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
// --- Types ---
interface Session {
  id: string;
  startedAt: string;
  endedAt?: string;
  status: 'active' | 'completed' | 'incomplete';
  readingsCount: number;
  user?: {
    id: string;
    fullName: string;
    email: string;
  };
  device?: {
    id: string;
    deviceUid: string;
  };
}
// --- Main Component ---
export function Sessions() {
  const {
    token
  } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Modals & Panels State
  const [viewSession, setViewSession] = useState<Session | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Live Timer State
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Fetch Data
  const fetchSessions = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        status: statusFilter === 'all' ? undefined : statusFilter,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        limit
      };
      const res = await getAllSessions(token, filters);
      setSessions(res.sessions || []);
      setTotalSessions(res.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  }, [token, debouncedSearch, statusFilter, dateFrom, dateTo, page, limit]);
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  const showToast = (message: string) => {
    setToast({
      message,
      type: 'success'
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor(diffMs % 3600000 / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (hours > 0) return `${hours}h ${mins}m`;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  return <div className="relative min-h-full pb-20" data-id="element-813">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-814">
        <div data-id="element-815">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-816">
            Monitoring Sessions
          </h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-817">
            {isLoading ? 'Loading...' : `${totalSessions.toLocaleString()} total sessions`}
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap" data-id="element-818">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-819">
          <div className="relative" data-id="element-820">
            <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-821" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search user or device..." className="w-[240px] h-[36px] bg-surface border border-border rounded-input pl-9 pr-4 text-[14px] text-white placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" data-id="element-822" />
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-823">
            <option value="all" data-id="element-824">All Status</option>
            <option value="active" data-id="element-825">Active</option>
            <option value="completed" data-id="element-826">Completed</option>
            <option value="incomplete" data-id="element-827">Incomplete</option>
          </select>

          <div className="flex items-center gap-2" data-id="element-828">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent [color-scheme:dark]" data-id="element-829" />
            <span className="text-text-secondary text-[14px]" data-id="element-830">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent [color-scheme:dark]" data-id="element-831" />
          </div>
        </div>

        <div className="text-[13px] text-text-secondary" data-id="element-832">
          Showing {sessions.length > 0 ? (page - 1) * limit + 1 : 0}-
          {Math.min(page * limit, totalSessions)} of {totalSessions}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-833">
        <div className="overflow-x-auto" data-id="element-834">
          <table className="w-full text-left border-collapse" data-id="element-835">
            <thead data-id="element-836">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-837">
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-838">
                  Session ID
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[200px]" data-id="element-839">
                  User
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[140px]" data-id="element-840">
                  Device
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[160px]" data-id="element-841">
                  Started
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-842">
                  Duration
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-843">
                  Status
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-844">
                  Readings
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[60px]" data-id="element-845">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-846">
              {isLoading ? <tr data-id="element-847">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-848">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" data-id="element-849" />
                    Loading sessions...
                  </td>
                </tr> : sessions.length === 0 ? <tr data-id="element-850">
                  <td colSpan={8} className="h-[200px] text-center text-text-secondary" data-id="element-851">
                    No sessions found matching your criteria.
                  </td>
                </tr> : sessions.map(session => <tr key={session.id} className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors" data-id="element-852">
                    <td className="px-4" data-id="element-853">
                      <div className="flex items-center gap-2" data-id="element-854">
                        <span className="text-[13px] font-mono text-white" data-id="element-855">
                          {session.id.substring(0, 8)}
                        </span>
                        <button onClick={() => copyToClipboard(session.id)} className="text-text-tertiary hover:text-white transition-colors" data-id="element-856">
                          <Copy className="w-3 h-3" data-id="element-857" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4" data-id="element-858">
                      {session.user ? <div className="flex items-center gap-2" data-id="element-859">
                          <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center text-background text-[10px] font-semibold flex-shrink-0" data-id="element-860">
                            {session.user.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex flex-col" data-id="element-861">
                            <span className="text-[13px] font-medium text-white truncate w-[140px]" data-id="element-862">
                              {session.user.fullName}
                            </span>
                            <span className="text-[11px] text-text-secondary truncate w-[140px]" data-id="element-863">
                              {session.user.email}
                            </span>
                          </div>
                        </div> : <span className="text-[13px] text-text-tertiary" data-id="element-864">
                          Unknown User
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-865">
                      {session.device ? <span className="text-[13px] font-mono text-white" data-id="element-866">
                          {session.device.deviceUid}
                        </span> : <span className="text-[13px] text-text-tertiary" data-id="element-867">
                          —
                        </span>}
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-868">
                      {new Date(session.startedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                    </td>
                    <td className="px-4" data-id="element-869">
                      {session.status === 'active' ? <div className="flex items-center gap-1.5" data-id="element-870">
                          <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" data-id="element-871" />
                          <span className="text-[13px] text-success font-medium" data-id="element-872">
                            {formatDuration(session.startedAt)}
                          </span>
                        </div> : <span className="text-[13px] text-text-secondary" data-id="element-873">
                          {formatDuration(session.startedAt, session.endedAt)}
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-874">
                      {session.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-875">
                          Active
                        </span>}
                      {session.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-876">
                          Completed
                        </span>}
                      {session.status === 'incomplete' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-877">
                          Incomplete
                        </span>}
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-878">
                      {session.readingsCount > 0 ? session.readingsCount.toLocaleString() : '—'}
                    </td>
                    <td className="px-4" data-id="element-879">
                      <button onClick={() => setViewSession(session)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-880">
                        <Eye className="w-4 h-4" data-id="element-881" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-882">
        <div className="flex items-center gap-2" data-id="element-883">
          <select value={limit} onChange={e => {
          setLimit(Number(e.target.value));
          setPage(1);
        }} className="h-[32px] bg-surface border border-border rounded-btn px-2 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-884">
            <option value={25} data-id="element-885">25 per page</option>
            <option value={50} data-id="element-886">50 per page</option>
            <option value={100} data-id="element-887">100 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2" data-id="element-888">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-889">
            <ChevronLeft className="w-4 h-4" data-id="element-890" /> Prev
          </button>

          <button className="w-[32px] h-[32px] bg-accent text-background rounded-btn text-[13px] font-medium flex items-center justify-center" data-id="element-891">
            {page}
          </button>

          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= totalSessions} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-892">
            Next <ChevronRight className="w-4 h-4" data-id="element-893" />
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className="fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top-5 bg-success text-white" data-id="element-894">
          <span className="text-[14px] font-medium" data-id="element-895">{toast.message}</span>
        </div>}

      {/* Detail Panel */}
      <SessionDetailPanel session={viewSession} onClose={() => setViewSession(null)} now={now} data-id="element-896" />
    </div>;
}
// --- Subcomponents ---
function SessionDetailPanel({
  session,
  onClose,
  now
}: {
  session: Session | null;
  onClose: () => void;
  now: Date;
}) {
  if (!session) return null;
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const hours = Math.floor(diffMs / 3600000);
    const mins = Math.floor(diffMs % 3600000 / 60000);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-897">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-898" />
      <div className="relative w-[480px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-899">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-900">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-901">
            <X className="w-5 h-5" data-id="element-902" />
          </button>
          <div className="flex items-center gap-4" data-id="element-903">
            <div className="w-12 h-12 rounded-full bg-[rgba(59,130,246,0.1)] flex items-center justify-center flex-shrink-0" data-id="element-904">
              <Activity className="w-6 h-6 text-info" data-id="element-905" />
            </div>
            <div data-id="element-906">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-907">
                Session Detail
              </h2>
              <p className="text-[13px] text-text-secondary font-mono mt-1" data-id="element-908">
                {session.id}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-909">
          {/* BPM placeholder */}
          <div className="bg-background border border-border rounded-card p-6 text-center" data-id="element-910">
            <p className="text-[13px] text-text-tertiary" data-id="element-911">
              Detailed BPM chart available in patient app
            </p>
          </div>

          {/* Info */}
          <div className="space-y-4" data-id="element-912">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-913">
              Information
            </h3>
            <div className="space-y-3" data-id="element-914">
              <div className="flex justify-between items-center" data-id="element-915">
                <span className="text-[13px] text-text-secondary" data-id="element-916">Status</span>
                {session.status === 'active' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-917">
                    Active
                  </span>}
                {session.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(138,138,138,0.1)] text-text-secondary text-[12px]" data-id="element-918">
                    Completed
                  </span>}
                {session.status === 'incomplete' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-919">
                    Incomplete
                  </span>}
              </div>
              <div className="flex justify-between" data-id="element-920">
                <span className="text-[13px] text-text-secondary" data-id="element-921">Started</span>
                <span className="text-[13px] text-white" data-id="element-922">
                  {new Date(session.startedAt).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-923">
                <span className="text-[13px] text-text-secondary" data-id="element-924">Ended</span>
                <span className="text-[13px] text-white" data-id="element-925">
                  {session.endedAt ? new Date(session.endedAt).toLocaleString() : '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-926">
                <span className="text-[13px] text-text-secondary" data-id="element-927">
                  Duration
                </span>
                <span className="text-[13px] text-white" data-id="element-928">
                  {formatDuration(session.startedAt, session.endedAt)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-929">
                <span className="text-[13px] text-text-secondary" data-id="element-930">
                  Total Readings
                </span>
                <span className="text-[13px] text-white" data-id="element-931">
                  {session.readingsCount.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Entities */}
          <div className="space-y-4" data-id="element-932">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-933">
              Entities
            </h3>
            <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-934">
              <div data-id="element-935">
                <p className="text-[11px] text-text-secondary mb-1" data-id="element-936">User</p>
                <p className="text-[13px] text-white" data-id="element-937">
                  {session.user?.fullName || 'Unknown'}
                </p>
              </div>
              <button className="text-[12px] text-accent hover:underline" data-id="element-938">
                View
              </button>
            </div>
            <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-939">
              <div data-id="element-940">
                <p className="text-[11px] text-text-secondary mb-1" data-id="element-941">Device</p>
                <p className="text-[13px] text-white font-mono" data-id="element-942">
                  {session.device?.deviceUid || 'Unknown'}
                </p>
              </div>
              <button className="text-[12px] text-accent hover:underline" data-id="element-943">
                View
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}